import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CreditCard, Minus, ShoppingCart, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { formatPrice } from '@/config/pricing';
import { useSubscription } from '@/contexts/billing/subscription';
import { useCurrency } from '@/contexts/currency/CurrencyContext';
import { useUserAgents } from '@/hooks/agent/useAgents';
import { useCallPurchases } from '@/hooks/billing/useCallPurchases';

interface PurchaseFormData {
  agentId: string;
  quantity: number;
}

interface CancelFormData {
  quantity: number;
}

export const CallPurchasesSection = () => {
  const { subscription } = useSubscription();
  const { callPurchases, isLoadingPurchases, purchaseCalls, cancelCalls, isPurchasing, isCanceling } = useCallPurchases();
  const { data: agents = [], isLoading: isLoadingAgents } = useUserAgents();
  const { currency, currencyConfig } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedPurchaseForCancel, setSelectedPurchaseForCancel] = useState<any>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PurchaseFormData>({
    defaultValues: { agentId: "", quantity: 1 },
  });
  const {
    register: registerCancel,
    handleSubmit: handleSubmitCancel,
    reset: resetCancel,
    watch: watchCancel,
    formState: { errors: cancelErrors },
  } = useForm<CancelFormData>({
    defaultValues: { quantity: 1 },
  });

  const selectedAgentId = watch("agentId");
  const selectedAgent = useMemo(() => agents.find((agent: any) => agent.id === selectedAgentId), [agents, selectedAgentId]);

  const dailyLimit = selectedAgent?.call_limit ?? 0;
  const pricePerCall = currencyConfig.displayPrices.additionalCall;
  const hasPaymentMethod = !!subscription?.stripe_id;

  const onSubmit = async (data: PurchaseFormData) => {
    if (!data.agentId || data.quantity < 1) {
      toast.error("Please select an agent and enter a valid quantity");
      return;
    }

    try {
      const result = await purchaseCalls(data.agentId, data.quantity);

      if (result.success) {
        toast.success(`Successfully purchased ${data.quantity} calls`);
        reset();
        setIsOpen(false);
      } else {
        toast.error(result.message || "Failed to purchase calls");
      }
    } catch (error) {
      console.error("Error purchasing calls:", error);
      toast.error("Failed to purchase calls");
    }
  };

  const onCancelSubmit = async (data: CancelFormData) => {
    if (!selectedPurchaseForCancel) {
      toast.error("No purchase selected");
      return;
    }

    const quantity = data.quantity > 0 ? data.quantity : undefined;

    try {
      const result = await cancelCalls(selectedPurchaseForCancel.agent_id, quantity);

      if (result.success) {
        const message = quantity ? `Successfully canceled ${quantity} calls` : "Successfully canceled all extra calls";
        toast.success(message);
        resetCancel();
        setCancelModalOpen(false);
        setSelectedPurchaseForCancel(null);
      } else {
        toast.error(result.message || "Failed to cancel calls");
      }
    } catch (error) {
      console.error("Error canceling calls:", error);
      toast.error("Failed to cancel calls");
    }
  };

  const openCancelModal = (purchase: any) => {
    setSelectedPurchaseForCancel(purchase);
    setCancelModalOpen(true);
    resetCancel({ quantity: 1 });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const PurchaseItem = ({ purchase }: { purchase: any }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex-1">
        {purchase.agents?.name && <div className="text-genie-primary font-medium">{purchase.agents.name}</div>}
        <div className="font-medium text-gray-900">{purchase.quantity} calls purchased</div>
        <div className="text-xs text-gray-500 capitalize">Status: {purchase.status || "active"}</div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <div className="text-sm text-gray-600">{formatDate(purchase.created_at)}</div>
        </div>
        {purchase.status === "active" && (
          <button
            onClick={() => openCancelModal(purchase)}
            disabled={isCanceling}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            title="Reduce calls"
          >
            <Minus className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );

  if (isLoadingPurchases) {
    return (
      <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center mb-[18px]">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <ShoppingCart className="h-5 w-5 text-genie-primary" />
          </div>
          <h2 className="text-xl font-semibold">Call Purchases</h2>
        </div>
        <div className="text-center text-gray-500">Loading call purchases...</div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center justify-between mb-[18px]">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
              <ShoppingCart className="h-5 w-5 text-genie-primary" />
            </div>
            <h2 className="text-xl font-semibold">Call Purchases</h2>
          </div>
          <Button
            onClick={() => setIsOpen(true)}
            disabled={!hasPaymentMethod}
            className="bg-genie-primary hover:bg-genie-primary/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            size="sm"
            title={!hasPaymentMethod ? "Add a payment method to purchase calls" : ""}
          >
            Add
          </Button>
        </div>

        {callPurchases.length === 0 ? (
          <div className="text-center text-gray-500 py-4">No call purchases yet.</div>
        ) : (
          <div className="space-y-3">
            {callPurchases.map((purchase) => (
              <PurchaseItem key={purchase.id} purchase={purchase} />
            ))}
          </div>
        )}
      </div>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="right" showClose={false} className="w-full sm:max-w-xl flex flex-col h-full p-0">
          <SheetTitle className="sr-only">Purchase Additional Calls</SheetTitle>
          <SheetDescription className="sr-only">Form to purchase additional calls for your genies</SheetDescription>
          <div className="flex items-center justify-between border-b p-6">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Purchase Additional Calls</h2>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto p-6 flex flex-col">
            <div className="space-y-4 flex-1">
              <div className="bg-genie-button/10 border border-genie-button/30 rounded-lg p-3 -mt-6">
                <p className="text-xs text-genie-button">Extra calls are charged every month and will renew on your billing cycle date.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Genie</label>
                <select
                  {...register("agentId", { required: "Please select a genie" })}
                  disabled={isLoadingAgents || isPurchasing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-genie-primary disabled:opacity-50"
                >
                  <option value="">{isLoadingAgents ? "Loading genies..." : "Select a genie"}</option>
                  {agents.map((agent: any) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
                {errors.agentId && <p className="text-red-600 text-xs mt-1">{errors.agentId.message}</p>}
              </div>

              {selectedAgent && (
                <>
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Current Daily Call Limit</label>
                    <div className="text-2xl font-bold text-gray-900">{dailyLimit}</div>
                  </div>
                  <div className="border-t border-gray-200"></div>
                </>
              )}

              <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Calls to Purchase
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="quantity"
                    type="number"
                    min="1"
                    step="1"
                    {...register("quantity", {
                      required: "Quantity is required",
                      min: { value: 1, message: "Quantity must be at least 1" },
                      valueAsNumber: true,
                    })}
                    disabled={isPurchasing}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  />
                  <div className="text-sm text-gray-500 whitespace-nowrap">
                    {formatPrice(pricePerCall, currencyConfig.code as any)} per call
                  </div>
                </div>
                {errors.quantity && <p className="text-red-600 text-xs mt-1">{errors.quantity.message}</p>}
              </div>

              {selectedAgent && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Total Cost</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatPrice(pricePerCall * watch("quantity"), currencyConfig.code as any)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>New Daily Limit</span>
                    <span className="font-semibold">{dailyLimit + watch("quantity")} calls</span>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={isPurchasing} className="flex-1">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isPurchasing}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold"
                >
                  {isPurchasing ? "Processing..." : "Purchase"}
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center">You will be charged using your existing payment method</p>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <Sheet open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <SheetContent side="right" showClose={false} className="w-full sm:max-w-xl flex flex-col h-full p-0">
          <SheetTitle className="sr-only">Reduce Calls</SheetTitle>
          <SheetDescription className="sr-only">Form to reduce or cancel extra calls for your genies</SheetDescription>
          <div className="flex items-center justify-between border-b p-6">
            <div className="flex items-center gap-2">
              <Minus className="w-5 h-5 text-red-600" />
              <h2 className="text-xl font-bold text-gray-900">Reduce Calls</h2>
            </div>
            <button onClick={() => setCancelModalOpen(false)} className="text-gray-500 hover:text-gray-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmitCancel(onCancelSubmit)} className="flex-1 overflow-auto p-6 flex flex-col">
            <div className="space-y-4 flex-1">
              {selectedPurchaseForCancel && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Genie</label>
                    <div className="text-2xl font-bold text-gray-900">{selectedPurchaseForCancel.agents?.name}</div>
                  </div>

                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Current Purchase</label>
                    <div className="text-2xl font-bold text-gray-900">{selectedPurchaseForCancel.quantity} calls</div>
                  </div>
                  <div className="border-t border-gray-200"></div>

                  <div>
                    <label htmlFor="quantity-cancel" className="block text-sm font-medium text-gray-700 mb-2">Quantity to Reduce</label>
                    <input
                      id="quantity-cancel"
                      type="number"
                      min="0"
                      step="1"
                      {...registerCancel("quantity", {
                        valueAsNumber: true,
                      })}
                      disabled={isCanceling}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      placeholder="0"
                    />
                    {cancelErrors.quantity && <p className="text-red-600 text-xs mt-1">{cancelErrors.quantity.message}</p>}
                    <p className="text-xs text-gray-500 mt-2">
                      Leave empty or enter 0 to cancel all {selectedPurchaseForCancel.quantity} calls
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setCancelModalOpen(false)} disabled={isCanceling} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isCanceling} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold">
                      {isCanceling ? "Processing..." : "Reduce Calls"}
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500 text-center">Changes will be applied to your next billing cycle</p>
                </>
              )}
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
};
